import { isNumericSeries } from "@incytestudios/ddv";
import type { NumericPoint, DataSet } from "@incytestudios/ddv";
import type { MultiScenarioPumpChartContext } from "./d";
import { convert, type ConvertableUnit } from "@/libs/units";
import { getFrequencyRatio } from "@/libs/calculations/pumps/pumpData";
import { getSimplifiedProducedTdh, isFinitePoint, isNaturalPoint } from "..";
import { getDensityCorrectedProducedPoints } from "@/libs/charting/_common/densityCorrection";

export const getProducedTdhDataSets = (
  context: MultiScenarioPumpChartContext
): DataSet[] => {
  const {
    scenarios,
    baseXUnit,
    targetXUnit,
    targetXUnitLabel,
    baseYUnit,
    targetYUnit,
    targetYUnitLabel,
    areScenariosEspShore,
    densityCorrectionRatioByScenarioIndex
  } = context;

  const xLabel = `Flow Rate (${targetXUnitLabel})`;
  let yLabel: string;
  if (areScenariosEspShore) {
    yLabel = `Total Dynamic Head (${targetYUnitLabel})`;
  } else {
    yLabel = `Differential Pressure (${targetYUnitLabel})`;
  }

  return scenarios
    .map((s, i) => {
      const {
        pumpCalculations,
        primaryPump,
        ascendingOrderPumps,
        dischargeFluidDensityLBMFT3,
        surfaceFlowRates
      } = s.calcs;
      const parameters = s.parameters;

      const operatingRPM = pumpCalculations.aggregate.operatingRPM.value;
      const graphMax = Math.min(
        ...ascendingOrderPumps.value.map(
          xrefPump => pumpCalculations.individual.value[xrefPump.id].graphMax
        )
      );
      const pumpIntakeFlowRateBBLD =
        pumpCalculations.individual.value[primaryPump.value?.id]
          ?.intakeStageCalculations?.intakeFluidPropertiesContext
          .flowRatesInSitu?.fluidFlowRateBBLD ?? 0;
      // Add liquid flow rate and pump intake rates from each scenario
      // to the hypothetical flow rates to ensure they are included in the chart
      const hypotheticalFlowRates = [
        ...Array.from({ length: 40 }, (_, i) => (graphMax / 40) * (i + 1)),
        surfaceFlowRates.value.liquidFlowRateSTBD,
        pumpIntakeFlowRateBBLD
      ].sort((a, b) => a - b);

      // Generate produced TDH points for each hypothetical flow rate
      // with the simplified produced tdh calculation
      const producedTDHData = hypotheticalFlowRates.map(
        (flowRateBblD: number) => {
          const frequencyRatio = getFrequencyRatio(
            operatingRPM,
            areScenariosEspShore
          );

          const data = getSimplifiedProducedTdh(
            flowRateBblD,
            frequencyRatio,
            ascendingOrderPumps.value,
            pumpCalculations.aggregate.gasHeadRerateRatio.value,
            parameters?.pump_wear_head_correction_ratio || 1,
            dischargeFluidDensityLBMFT3.value
          );

          return {
            x: flowRateBblD,
            tdhFt: data.tdh,
            tdhPressurePSIA: data.tdhPressure
          };
        }
      );
      const originalProducedTDHPoints = producedTDHData.map<NumericPoint>(
        d => ({
          x: d.x,
          y: areScenariosEspShore ? d.tdhFt : d.tdhPressurePSIA
        })
      );
      const densityCorrectionRatio = densityCorrectionRatioByScenarioIndex[i];
      const correctedProducedTDHPoints = getDensityCorrectedProducedPoints(
        originalProducedTDHPoints,
        densityCorrectionRatio
      );

      return {
        title: `${s.scenario?.title}`,
        xAxisLabel: xLabel,
        yAxisLabel: yLabel,
        color: `var(--plotColor${i})`,
        points: correctedProducedTDHPoints
      };
    })
    .map(ds => {
      return dataSetToUserUnits(
        ds,
        baseXUnit,
        targetXUnit,
        baseYUnit,
        targetYUnit,
        xLabel,
        yLabel
      );
    });
};

export function dataSetToUserUnits(
  dataSet: DataSet,
  baseXUnit: ConvertableUnit,
  targetXUnit: ConvertableUnit,
  baseYUnit: ConvertableUnit,
  targetYUnit: ConvertableUnit,
  xLabel: string,
  yLabel: string
): DataSet {
  const numericPoints = isNumericSeries(dataSet.points) ? dataSet.points : [];
  return {
    ...dataSet,
    xAxisLabel: xLabel,
    yAxisLabel: yLabel,
    points: numericPoints
      .map(p => {
        return {
          x: convert(p.x, baseXUnit, targetXUnit),
          y: convert(p.y, baseYUnit, targetYUnit)
        };
      })
      .filter(isFinitePoint)
      .filter(isNaturalPoint)
  };
}
